import { useState, useCallback, useMemo, useEffect } from "react";
import {
  UniversalTable,
  TableColumnsConfig,
} from "shared/components/UniversalTable";
import { TableSortConfig } from "shared/components/UniversalTable/types/SortConfig";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Plus } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  ACCESS_CONTROL_COLUMNS,
  ACCESS_CONTROL_FILTER_SECTIONS,
  ACCESS_CONTROL_GROUPBY_OPTIONS,
} from "./config/accessControlTableConfig";
import { createAccessControlCells } from "./config/accessControlCellFactory";
import AddAccessControlModal from "./components/AddAccessControlModal";
import AccessControlDetailModal from "./components/AccessControlDetailModal";
import { AccessControlItem } from "./types/accessControl";

const AccessControlPage = () => {
  const { session } = useSession();

  const [items, setItems] = useState<AccessControlItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filter & grouping state
  const [searchText, setSearchText] = useState("");
  const [accessTypeFilters, setAccessTypeFilters] = useState<string[]>([]);
  const [buildingFilters, setBuildingFilters] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<string>("none");

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<AccessControlItem | null>(null);

  const fetchAccessControls = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/access-control`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setItems(data);
    } catch (error) {
      console.error("Failed to load access controls:", error);
      toast.error("Fout bij het laden van toegangscontrole.");
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      fetchAccessControls();
    }
  }, [session, fetchAccessControls]);

  // Build filter sections with dynamic building options from data
  const filterSectionsWithData = useMemo(() => {
    return ACCESS_CONTROL_FILTER_SECTIONS.map((section) => {
      if (section.extractOptionsFromData) {
        return {
          ...section,
          options: section.extractOptionsFromData(items),
        };
      }
      return section;
    });
  }, [items]);

  // Handle filter changes
  const handleFilterChange = useCallback(
    (key: string, values: string[]) => {
      switch (key) {
        case "accessType":
          setAccessTypeFilters(values);
          break;
        case "building":
          setBuildingFilters(values);
          break;
      }
    },
    [],
  );

  // Client-side filtering
  const filteredItems = useMemo(() => {
    let result = items;

    if (searchText) {
      const search = searchText.toLowerCase();
      result = result.filter((row) => {
        return (
          (row.building?.id || row.buildingId || "").toLowerCase().includes(search) ||
          (row.unit?.id || row.unitId || "").toLowerCase().includes(search) ||
          (row.accessType || "").toLowerCase().includes(search) ||
          (row.keyLocation || "").toLowerCase().includes(search) ||
          (row.additionalInfo || "").toLowerCase().includes(search)
        );
      });
    }

    if (accessTypeFilters.length > 0) {
      result = result.filter(
        (row) => row.accessType && accessTypeFilters.includes(row.accessType),
      );
    }

    if (buildingFilters.length > 0) {
      result = result.filter(
        (row) =>
          (row.building?.id || row.buildingId) &&
          buildingFilters.includes(row.building?.id || row.buildingId),
      );
    }

    return result;
  }, [items, searchText, accessTypeFilters, buildingFilters]);

  const handleViewDetail = useCallback((item: AccessControlItem) => {
    setSelectedItem(item);
    setDetailModalOpen(true);
  }, []);

  // Render row using cell factory
  const renderRow = useCallback(
    (row: AccessControlItem) => {
      return createAccessControlCells(row, {
        onViewDetail: handleViewDetail,
      });
    },
    [handleViewDetail],
  );

  // Sort config
  const sortConfig: TableSortConfig = useMemo(
    () => ({
      fields: [
        { key: "building", extractor: (row: AccessControlItem) => row.building?.id || row.buildingId || "" },
        { key: "unit", extractor: (row: AccessControlItem) => row.unit?.id || row.unitId || "" },
        { key: "accessType", extractor: (row: AccessControlItem) => row.accessType || "" },
        { key: "keyLocation", extractor: (row: AccessControlItem) => row.keyLocation || "" },
        { key: "additionalInfo", extractor: (row: AccessControlItem) => row.additionalInfo || "" },
      ],
      defaultSortKey: "building",
      defaultDirection: "asc",
    }),
    [],
  );

  return (
    <HelmetProvider>
      <Helmet>
        <title>Toegangscontrole &mdash; Yally</title>
      </Helmet>

      <div className="flex h-full flex-col">
        <div className="flex items-center justify-end px-4 pt-4">
          <ButtonNew
            variant="primaryMoss"
            onClick={() => setIsModalOpen(true)}
            icon={<Plus />}
            label="Toegangscontrole toevoegen"
          />
        </div>

        <UniversalTable
          columnsConfig={
            {
              columns: ACCESS_CONTROL_COLUMNS,
              sortConfig: {
                defaultSortKey: "building",
                defaultDirection: "asc",
              } as TableSortConfig,
            } as TableColumnsConfig
          }
          tableId="accesscontrol-v1"
          rows={filteredItems}
          title="Toegangscontrole"
          description="Overzicht van alle toegangscontroles"
          groupBy={groupBy}
          isLoading={isLoading}
          sortConfig={sortConfig}
          searchConfig={{
            searchPlaceholder: "Zoeken op gebouw, unit, type, sleutel...",
            filterSections: filterSectionsWithData,
            groupByOptions: ACCESS_CONTROL_GROUPBY_OPTIONS,
            onGroupByChange: setGroupBy,
            searchValue: searchText,
            onSearchChange: setSearchText,
            activeFilters: {
              accessType: accessTypeFilters,
              building: buildingFilters,
            },
            onFilterChange: handleFilterChange,
          }}
          renderRow={renderRow}
        />
      </div>

      <AddAccessControlModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={fetchAccessControls}
      />

      {selectedItem && (
        <AccessControlDetailModal
          isOpen={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedItem(null);
          }}
          item={selectedItem}
        />
      )}
    </HelmetProvider>
  );
};

export default AccessControlPage;
